"use client";

import { motion } from "framer-motion";
import { ArrowLeft, Shield, Lock, Eye, Cookie, Users, Globe } from "lucide-react";
import Link from "next/link";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";

const sections = [
  {
    id: "collection",
    title: "1. Information Collection and Usage",
    icon: <Eye size={20} className="text-[#B8893A]"/>,
    content: [
      "While using our Website, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Therefore, we may gather two types of information about you:",
      {
        subtitle: "(a) Personal information",
        text: "Personal information including but not limited to, your name, address, phone number, email address, date of birth, and payment information, which may be collected from you when you voluntarily provide the same through methods including but not limited to:",
        list: [
          "Registering and creating a membership account;",
          "Participating in surveys and other promotions available on the Website;",
          "Communicating with us through the Contact Us information provided on our Website, through Customer Service Live Chat, or via telephone;",
          "Interaction with us on our social media platforms (such as Facebook, Instagram, Twitter, LinkedIn) or Website;",
          "Other sources such as third parties where it is reasonably necessary for our normal business practices to do so."
        ]
      },
      {
        subtitle: "(b) Aggregate information",
        text: "Aggregate information is generated by AWS's systems, which is not directly related to you personally. Whenever you interact with AWS Products and/or Services, we automatically receive and record information and data on our server logs from your browser or device, which may include your IP address, device identification 'cookie' information, the type of browser and/or device you're using, and the page or feature you requested. AWS uses this data to improve our Products and/or Services and enhance the user experience for our customers."
      },
      "Failure to provide necessary personal information when requested may result in certain services not being available to you."
    ]
  },
  {
    id: "disclosure",
    title: "2. Disclosure of Information",
    icon: <Users size={20} className="text-[#B8893A]" />,
    content: [
      "AWS may disclose Personal Information about you provided you have granted authority for the following:",
      {
        list: [
          "To our affiliates worldwide, licensees, and joint venture partners for the purposes described in this Policy;",
          "To third parties for the purposes of providing you with services that you request;",
          "Aggregate tracking information and other information that does not personally identify you to third parties;",
          "When AWS believes in good faith that it is required to do so by law or legal process."
        ]
      }
    ]
  },
  {
    id: "use",
    title: "3. Use of Information",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "AWS shall use the personal information it collects:",
      {
        list: [
          "To verify your identity and assist you with login details;",
          "To assist in providing the services requested by you;",
          "To provide information about other services which AWS considers may be of interest to you;",
          "For future marketing, promotional, and publicity purposes;",
          "For internal development, data analytics, and compliance with legal obligations;",
          "To prevent and detect any misuse or fraudulent activities."
        ]
      }
    ]
  },
  {
    id: "cookies",
    title: "4. Cookies and Tracking",
    icon: <Cookie size={20} className="text-[#B8893A]" />,
    content: [
      "This Website uses 'Cookies' to identify details of your IP address, operating system, browser type, and locale. AWS uses this information to track traffic patterns and ensure advertising is appropriate. You may choose to disable cookies in your browser settings, though some features of the Website may not function properly as a result.",
      "AWS's use of Google Analytics is in compliance with Google's Terms of Service. Information collected through Google Analytics does not include personal information and is used solely to evaluate website usage and improve user experience."
    ]
  },
  {
    id: "third-party",
    title: "5. Content Providers, Advertisers and Partners",
    icon: <Globe size={20} className="text-[#B8893A]" />,
    content: [
      "This Policy does not apply to third-party websites linked on our Website. AWS is not responsible for the data collection practices or privacy policies of external sites."
    ]
  },
  {
    id: "opting",
    title: "6. Opting In and Out",
    icon: <Lock size={20} className="text-[#B8893A]" />,
    content: [
      "If AWS intends to use your information outside the scope of this Policy, we will provide you the option to:",
      {
        list: [
          "Opt out of certain services or interactive areas;",
          "Opt in to be contacted regarding new features or promotional activities."
        ]
      }
    ]
  },
  {
    id: "protection",
    title: "7. Protecting your Personal Information",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "AWS recognizes industry standards and practices appropriate administrative, technical, and physical security safeguards to protect your data. We use secure servers and encryption (such as TLS 1.2) for sensitive information.",
      "While we strive to protect your personal information, no method of transmission over the Internet is 100% secure; therefore, AWS cannot guarantee absolute security and shall not be held liable for unauthorized access."
    ]
  },
  {
    id: "overseas",
    title: "8. Disclosure of Information Overseas",
    icon: <Globe size={20} className="text-[#B8893A]" />,
    content: [
      "We may require the transfer of your personal information to countries other than the one in which you originally provided it. When we do so, AWS shall protect that information as described in this Privacy Policy and in accordance with applicable laws."
    ]
  },
  {
    id: "holding",
    title: "9. Holding, Correcting and Updating Information",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "AWS allows you to correct or change your information at any time, except for certain restricted data fields (like country of residence). To amend these or ask questions about our services, please contact the AWS support team."
    ]
  },
  {
    id: "amendments",
    title: "10. Amendments",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "AWS reserves the right to change this Privacy Policy at any time without prior notice. Your continued use of the Website constitutes acceptance of the modified Policy."
    ]
  },
  {
    id: "acceptance",
    title: "11. Acceptance",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "By using the AWS Businessmen Services website, you signify your acceptance of this Policy."
    ]
  },
  {
    id: "contact",
    title: "12. How to Contact Us",
    icon: <Shield size={20} className="text-[#B8893A]" />,
    content: [
      "If you have any questions or comments regarding our privacy practices, please contact us via the contact details provided on our Website."
    ]
  }
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      {/* Header */}
      <section className="bg-[#fafafa] border-b border-gray-100 pt-20">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <div className="flex items-center gap-4 mb-6">
            <Link
              href="/"
              className="flex items-center gap-2 text-[#B8893A] hover:text-[#A67B34] transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-black text-[#0A0A0A] mb-4 tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              The following Privacy Policy (hereinafter the "Policy") governs the manner in which AWS Businessmen Services, and its bodies corporate and affiliates (collectively known as "AWS") collect, use, maintain and disclose personal information and explains how you can access your own personal information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12 p-6 rounded-xl bg-gradient-to-r from-[#B8893A]/5 to-[#D4A754]/5 border border-[#B8893A]/20"
          >
            <p className="text-gray-700 leading-relaxed mb-4">
              This Policy is applicable to personal information and aggregated information we collect on the AWS Businessmen Services website (hereinafter the "Website"), which must be read in conjunction with the Website's Terms of Use.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Where this Policy refers to "we", "us" and "our" we mean AWS, and where this Policy refers to "you" and "your" we mean the user of the Website.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You undertake, including but not limited to, that by viewing, using the Website, or obtaining any goods or services from us, you shall be deemed to agree to this Policy. Should you fail to comply with this Policy, then you must refrain from using or accessing the Website.
            </p>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-[#fafafa] rounded-xl border border-gray-200 p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-6">
                  {section.icon}
                  <h2 className="text-xl font-bold text-[#0A0A0A]">{section.title}</h2>
                </div>

                <div className="space-y-4 text-gray-700 leading-relaxed">
                  {section.content.map((item, i) => {
                    if (typeof item === 'string') {
                      return <p key={i}>{item}</p>;
                    } else if (item.subtitle) {
                      return (
                        <div key={i} className="ml-4">
                          <h3 className="font-semibold text-[#0A0A0A] mb-2">{item.subtitle}</h3>
                          <p className="mb-3">{item.text}</p>
                          {item.list && (
                            <ul className="list-disc list-inside space-y-1 ml-4">
                              {item.list.map((listItem, j) => (
                                <li key={j} className="text-gray-600">{listItem}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    } else if (item.list) {
                      return (
                        <ul key={i} className="list-disc list-inside space-y-2 ml-4">
                          {item.list.map((listItem, j) => (
                            <li key={j} className="text-gray-600">{listItem}</li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-[#B8893A]/5 to-[#D4A754]/5 border border-[#B8893A]/20 text-center"
          >
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">Questions About Our Privacy Policy?</h3>
            <p className="text-gray-600 mb-6">
              If you have any questions or concerns about how we handle your personal information, please don't hesitate to contact us.
            </p>
            <Link
              href="/contact"
              className="inline-block px-8 py-3 rounded-xl bg-[#B8893A] text-white font-bold shadow-lg hover:bg-[#A67B34] transition-all hover:scale-105 text-sm"
            >
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
